import React, { useState } from 'react';
import {
  Box,
  Fab,
  TextField,
  Button,
  Paper,
  Typography,
  IconButton,
  Slide,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  MessageCircle as MessageIcon,
  X as XIcon,
  Send as SendIcon,
} from 'lucide-react';
import { submitFeedback, getApiErrorMessage } from '../services/api';

function FeedbackWidget({ isOpen, onOpen, onClose, hideFab }) {
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleOpen = () => {
    setSuccess(false);
    setError(null);
    onOpen();
  };

  const handleClose = () => {
    setMessage('');
    setSuccess(false);
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!message.trim()) return;

    setSubmitting(true);
    setError(null);

    try {
      await submitFeedback(message.trim(), 'general');

      setSuccess(true);
      setMessage('');
      
      // Auto-close after 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      console.error('Feedback submission failed:', err);
      setError(getApiErrorMessage(err, 'Failed to submit feedback. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <>
      {/* FAB - Position 1 (rightmost) */}
      {!hideFab && (
        <Fab
          variant="extended"
          color="primary"
          onClick={handleOpen}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
            gap: 1,
            px: 2,
          }}
        >
          <MessageIcon size={20} />
          Feedback
        </Fab>
      )}

      {/* Panel - Same position/size for all widgets */}
      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: { xs: 24, sm: 32 },
            right: { xs: 12, sm: 24 },
            left: { xs: 12, sm: 'auto' },
            width: { xs: 'calc(100% - 24px)', sm: 360 },
            maxWidth: { xs: 'calc(100% - 24px)', sm: 360 },
            zIndex: 1200,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MessageIcon size={20} color="white" />
              <Typography variant="h6" fontWeight="bold" color="white">
                Feedback
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{ color: 'white' }}
            >
              <XIcon size={20} />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ p: 2 }}>
            {success ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Thanks for your feedback!
              </Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Found a bug? Have a feature idea? Let us know!
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Type your feedback here..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={submitting}
                  sx={{ mb: 2 }}
                  inputProps={{ inputMode: 'text', enterKeyHint: 'send' }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleSubmit}
                  disabled={!message.trim() || submitting}
                  startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon size={20} />}
                >
                  {submitting ? 'Sending...' : 'Send Feedback'}
                </Button>
              </>
            )}
          </Box>
        </Paper>
      </Slide>
    </>
  );
}

export default FeedbackWidget;
